/** Clave de serie ARAMIS en los datos de Recharts. */
export const ARAMIS_DATA_KEY = "aramis";
export const ARAMIS_LABEL = "ARAMIS";

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const monthValue = (serie, month) => {
  const v = serie?.mes?.[month];
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const yearTotal = (serie) => {
  if (serie == null) return 0;
  if (Number.isFinite(Number(serie.total))) {
    return Number(serie.total);
  }
  return MONTHS.reduce((acc, m) => acc + monthValue(serie, m), 0);
};

/**
 * Pesos mensuales de estacionalidad a partir de años históricos completos.
 * Los años más recientes tienen más peso (factor de decaimiento exponencial 0.5 por año).
 * Esto evita que años atípicos o lejanos distorsionen la proyección.
 */
export const buildSeasonalityWeights = (serieByYear, priorYears) => {
  const years = (Array.isArray(priorYears) ? priorYears : [])
    .filter((y) => {
      const total = Math.abs(yearTotal(serieByYear?.[y]));
      return total > 0;
    })
    .sort((a, b) => a - b); // ascendente: el más reciente tiene índice más alto

  if (!years.length) {
    return Object.fromEntries(MONTHS.map((m) => [m, 1 / 12]));
  }

  // Factor de decaimiento: el año más reciente tiene peso 1, el anterior 0.5, el anterior 0.25…
  const DECAY = 0.5;
  const maxYear = years[years.length - 1];
  const yearWeights = Object.fromEntries(
    years.map((y) => [y, Math.pow(DECAY, maxYear - y)]),
  );
  const totalYearWeight = years.reduce((acc, y) => acc + yearWeights[y], 0);

  const sums = Object.fromEntries(MONTHS.map((m) => [m, 0]));
  years.forEach((year) => {
    const total = yearTotal(serieByYear[year]);
    if (!total) return;
    const w = yearWeights[year] / totalYearWeight;
    MONTHS.forEach((m) => {
      sums[m] += w * (monthValue(serieByYear[year], m) / total);
    });
  });

  const rawSum = MONTHS.reduce((acc, m) => acc + sums[m], 0);
  if (!rawSum) {
    return Object.fromEntries(MONTHS.map((m) => [m, 1 / 12]));
  }
  return Object.fromEntries(MONTHS.map((m) => [m, sums[m] / rawSum]));
};

const sumWeights = (weights, fromMonth, toMonth) => {
  let s = 0;
  for (let m = fromMonth; m <= toMonth; m += 1) s += weights[m] || 0;
  return s;
};

const sumActual = (serie, fromMonth, toMonth) => {
  let s = 0;
  for (let m = fromMonth; m <= toMonth; m += 1) s += monthValue(serie, m);
  return s;
};

const estimateAnnual = (ytdActual, ytdWeight, fallbackAnnual) => {
  if (ytdWeight > 1e-9) return ytdActual / ytdWeight;
  return fallbackAnnual;
};

/**
 * Proyección ARAMIS para el año en curso:
 * - Meses cerrados: backtest (qué hubiera dicho el modelo)
 * - Mes actual y siguientes: proyección por estacionalidad escalada al YTD
 *
 * @returns {{ monthly: Record<number, number>, asOfMonth: number, targetYear: number } | null}
 */
export const buildAramisProjection = (
  serieByYear,
  {
    targetYear = new Date().getFullYear(),
    asOfMonth = new Date().getMonth() + 1,
    compareYears = [],
  } = {},
) => {
  if (!serieByYear || typeof serieByYear !== "object") return null;
  if (!serieByYear[targetYear]) return null;

  const month = Math.min(12, Math.max(1, Number(asOfMonth) || 1));
  const yearsInData = Object.keys(serieByYear)
    .map(Number)
    .filter((y) => Number.isFinite(y));
  const pool = (
    Array.isArray(compareYears) && compareYears.length
      ? compareYears
      : yearsInData
  ).map(Number);
  const priorYears = [...new Set(pool)]
    .filter((y) => y < targetYear)
    .sort((a, b) => a - b);

  const weights = buildSeasonalityWeights(serieByYear, priorYears);
  const current = serieByYear[targetYear];
  const fallbackAnnual =
    priorYears.length > 0
      ? priorYears.reduce((acc, y) => acc + yearTotal(serieByYear[y]), 0) /
        priorYears.length
      : yearTotal(current);

  const monthly = {};

  // Backtest: para cada mes cerrado, estimar con info disponible hasta el mes anterior
  for (let m = 1; m < month; m += 1) {
    if (m === 1) {
      monthly[m] = fallbackAnnual * (weights[m] || 0);
      continue;
    }
    const ytd = sumActual(current, 1, m - 1);
    const w = sumWeights(weights, 1, m - 1);
    const annual = estimateAnnual(ytd, w, fallbackAnnual);
    monthly[m] = annual * (weights[m] || 0);
  }

  // Forward: desde el mes actual (inclusive) hasta diciembre
  const closedEnd = month - 1;
  const ytdClosed = closedEnd >= 1 ? sumActual(current, 1, closedEnd) : 0;
  const wClosed = closedEnd >= 1 ? sumWeights(weights, 1, closedEnd) : 0;
  const annualForward = estimateAnnual(ytdClosed, wClosed, fallbackAnnual);

  for (let m = month; m <= 12; m += 1) {
    monthly[m] = annualForward * (weights[m] || 0);
  }

  return { monthly, asOfMonth: month, targetYear, annualForward };
};
